import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// L'écran des produits a une résolution fixe : on ne peut pas le rendre responsive, seulement
// le mettre à l'échelle (et le pivoter) pour qu'il tienne dans la place disponible.
const SCREEN = { width: 1280, height: 800 };

type Fit = { scale: number; rotated: boolean };

/**
 * Plus grande échelle (plafonnée à 1, au-delà l'image devient floue) qui fait tenir l'écran
 * dans `width × height`. Une zone plus haute que large — un téléphone en portrait — reçoit
 * l'écran pivoté d'un quart de tour, sinon il n'occuperait qu'un tiers de la hauteur.
 */
export const fitScreen = (width: number, height: number): Fit => {
  const rotated = height > width;
  const [w, h] = rotated
    ? [SCREEN.height, SCREEN.width]
    : [SCREEN.width, SCREEN.height];
  return { rotated, scale: Math.max(0, Math.min(1, width / w, height / h)) };
};

/** Remplit son parent flex : celui-ci doit avoir une hauteur bornée. */
export const FrameDevice = ({ url, name }: { url: string; name: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<Fit>({ scale: 0, rotated: false });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const update = () => setFit(fitScreen(el.clientWidth, el.clientHeight));

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { scale, rotated } = fit;
  // Empreinte réelle dans le flux : `transform` ne réduit pas la boîte qu'il transforme.
  const footprint = rotated
    ? { width: SCREEN.height * scale, height: SCREEN.width * scale }
    : { width: SCREEN.width * scale, height: SCREEN.height * scale };

  return (
    <div
      ref={containerRef}
      className="flex-1 min-h-0 min-w-0 flex justify-center items-start overflow-hidden"
    >
      <div
        className="relative shrink-0 overflow-hidden rounded-b-lg"
        style={footprint}
      >
        <iframe
          src={url}
          width={SCREEN.width}
          height={SCREEN.height}
          title={name}
          className="absolute top-0 left-0 border-0"
          style={{
            // Lu de droite à gauche : remonter d'une hauteur, pivoter autour du coin haut
            // gauche (l'écran retombe dans le quart positif), puis réduire.
            transform: rotated
              ? `scale(${scale}) rotate(90deg) translateY(-100%)`
              : `scale(${scale})`,
            transformOrigin: "top left",
          }}
        />
      </div>
    </div>
  );
};

export const EmptyFramePlaceholder = () => {
  const { t } = useTranslation();
  return (
    <div className="flex-1 min-h-0 flex items-center justify-center text-grey-medium text-base font-oxaniumMedium">
      {t("remote-view-empty-slot")}
    </div>
  );
};
